<script setup lang="ts">
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const { t } = useI18n()
const table = 'organization-subscriptions'

const headers = [
  { title: t(`${table}-page.organization`), key: 'name' },
  { title: t(`${table}-page.host`), key: 'host' },
  { title: t(`${table}-page.current_plan`), key: 'plan', sortable: false },
  { title: t(`${table}-page.status`), key: 'status', sortable: false },
  { title: t(`${table}-page.expires_at`), key: 'expires_at', sortable: false },
  { title: t(`${table}-page.actions`), key: 'actions', sortable: false },
]

const searchQuery = ref('')
const debounced = useDebounce(searchQuery, 300)

const tableOptions = useCookie<any>(`${table}:options`, {
  default: () => ({
    page: 1,
    itemsPerPage: 10,
    sortBy: 'id',
    orderBy: 'desc',
  }),
})

const query = computed(() => ({
  search: debounced.value,
  page: tableOptions.value.page,
  itemsPerPage: tableOptions.value.itemsPerPage,
  sortBy: tableOptions.value.sortBy,
  orderBy: tableOptions.value.orderBy,
}))

const url = createUrl('/organizations/', { query })
const { data: orgsData } = await useApi<any>(url)

const items = computed(() => orgsData.value?.data ?? [])
const total = computed(() => orgsData.value?.total ?? 0)
const isLoading = ref(false)

// Plan list for assign/switch dialogs
const { data: plansData } = await useApi<any>('/subscription/plans?itemsPerPage=100&page=1&sortBy=name&orderBy=asc')
const plans = computed(() => plansData.value?.data ?? [])

// Subscription data per org
const orgSubscriptions = ref<Record<string, any>>({})

const fetchOrgSubscription = async (orgId: string) => {
  try {
    const data = await $api(`/subscription/organizations/${orgId}/subscription`)
    orgSubscriptions.value[orgId] = data
  }
  catch {
    orgSubscriptions.value[orgId] = null
  }
}

// Fetch subscriptions for visible orgs
watch(items, async (newItems) => {
  for (const item of newItems) {
    if (!(item.id in orgSubscriptions.value)) {
      fetchOrgSubscription(item.id)
    }
  }
}, { immediate: true })

// Assign/Switch dialog
const isAssignDialogOpen = ref(false)
const selectedOrgId = ref<string | null>(null)
const selectedPlanId = ref<number | null>(null)
const isSwitchMode = ref(false)
const switchImmediately = ref(true)

const initialConsumption = ref<Record<string, number>>({})

const selectedPlanFeatures = computed(() => {
  if (!selectedPlanId.value)
    return []

  const plan = plans.value.find((p: any) => p.id === selectedPlanId.value)

  if (!plan?.features)
    return []

  return featureOptions.filter(opt =>
    plan.features.some((f: any) => f.feature === opt.value && f.charges > 0),
  )
})

const openAssignDialog = (orgId: string, isSwitch = false) => {
  selectedOrgId.value = orgId
  selectedPlanId.value = null
  isSwitchMode.value = isSwitch
  switchImmediately.value = true
  initialConsumption.value = {}
  isAssignDialogOpen.value = true
}

const handleAssign = async () => {
  if (!selectedOrgId.value || !selectedPlanId.value) {
    return
  }

  try {
    if (isSwitchMode.value) {
      await $api(`/subscription/organizations/${selectedOrgId.value}/switch`, {
        method: 'PUT',
        body: { plan_id: selectedPlanId.value, immediately: switchImmediately.value },
      })
    }
    else {
      const body: Record<string, unknown> = { plan_id: selectedPlanId.value }
      const filtered = Object.fromEntries(
        Object.entries(initialConsumption.value).filter(([, v]) => v > 0),
      )
      if (Object.keys(filtered).length > 0)
        body.initial_consumption = filtered

      await $api(`/subscription/organizations/${selectedOrgId.value}/subscribe`, {
        method: 'POST',
        body,
      })
    }

    await fetchOrgSubscription(selectedOrgId.value)
    isAssignDialogOpen.value = false
  }
  catch (e) {
    console.error('Failed to assign/switch plan', e)
  }
}

// Cancel dialog
const isCancelDialogOpen = ref(false)
const cancelOrgId = ref<string | null>(null)

const openCancelDialog = (orgId: string) => {
  cancelOrgId.value = orgId
  isCancelDialogOpen.value = true
}

const handleCancel = async (confirmed: boolean) => {
  if (confirmed && cancelOrgId.value) {
    try {
      await $api(`/subscription/organizations/${cancelOrgId.value}/subscription`, { method: 'DELETE' })
      await fetchOrgSubscription(cancelOrgId.value)
    }
    catch (e) {
      console.error('Failed to cancel subscription', e)
    }
  }
}

// Renew dialog
const isRenewDialogOpen = ref(false)
const renewOrgId = ref<string | null>(null)

const openRenewDialog = (orgId: string) => {
  renewOrgId.value = orgId
  isRenewDialogOpen.value = true
}

const handleRenew = async (confirmed: boolean) => {
  if (confirmed && renewOrgId.value) {
    try {
      await $api(`/subscription/organizations/${renewOrgId.value}/renew`, { method: 'POST' })
      await fetchOrgSubscription(renewOrgId.value)
    }
    catch (e) {
      console.error('Failed to renew subscription', e)
    }
  }
}

// Usage dialog
const isUsageDialogOpen = ref(false)
const usageOrgId = ref<string | null>(null)
const usageData = ref<any[]>([])

const openUsageDialog = async (orgId: string) => {
  usageOrgId.value = orgId

  try {
    usageData.value = await $api(`/subscription/organizations/${orgId}/usage`) as any
  }
  catch {
    usageData.value = []
  }

  await fetchTickets(orgId)
  isUsageDialogOpen.value = true
}

const getSubscription = (orgId: string) => orgSubscriptions.value[orgId]

const statusColor = (status: string | null) => {
  if (!status) {
    return 'secondary'
  }

  const colors: Record<string, string> = {
    active: 'success',
    cancelled: 'warning',
    expired: 'error',
    tickets_only: 'info',
  }

  return colors[status] ?? 'secondary'
}

// Feature options for ticket dialog
const featureOptions = [
  { title: t('subscription.regular_games'), value: 'regular_games' },
  { title: t('subscription.ai_games'), value: 'ai_games' },
  { title: t('subscription.any_games'), value: 'any_games' },
  { title: t('subscription.ai_chat_messages'), value: 'ai_chat_messages' },
]

// Add Ticket dialog
const isTicketDialogOpen = ref(false)
const ticketOrgId = ref<string | null>(null)
const ticketFeature = ref<string | null>(null)
const ticketCharges = ref<number>(1)
const ticketExpiredAt = ref<string | null>(null)

const openTicketDialog = (orgId: string) => {
  ticketOrgId.value = orgId
  ticketFeature.value = null
  ticketCharges.value = 1
  ticketExpiredAt.value = null
  isTicketDialogOpen.value = true
}

const handleAddTicket = async () => {
  if (!ticketOrgId.value || !ticketFeature.value) {
    return
  }

  try {
    await $api(`/subscription/organizations/${ticketOrgId.value}/tickets`, {
      method: 'POST',
      body: {
        feature: ticketFeature.value,
        charges: ticketCharges.value,
        expired_at: ticketExpiredAt.value || null,
      },
    })

    await fetchOrgSubscription(ticketOrgId.value)
    isTicketDialogOpen.value = false
  }
  catch (e) {
    console.error('Failed to add ticket', e)
  }
}

// Tickets in usage dialog
const ticketsData = ref<any[]>([])
const isRevokeTicketDialogOpen = ref(false)
const revokeTicketId = ref<number | null>(null)
const revokeTicketOrgId = ref<string | null>(null)

const fetchTickets = async (orgId: string) => {
  try {
    ticketsData.value = await $api(`/subscription/organizations/${orgId}/tickets`) as any
  }
  catch {
    ticketsData.value = []
  }
}

const openRevokeTicketDialog = (orgId: string, ticketId: number) => {
  revokeTicketOrgId.value = orgId
  revokeTicketId.value = ticketId
  isRevokeTicketDialogOpen.value = true
}

const handleRevokeTicket = async (confirmed: boolean) => {
  if (confirmed && revokeTicketOrgId.value && revokeTicketId.value) {
    try {
      await $api(`/subscription/organizations/${revokeTicketOrgId.value}/tickets/${revokeTicketId.value}`, {
        method: 'DELETE',
      })

      await fetchTickets(revokeTicketOrgId.value)
      await fetchOrgSubscription(revokeTicketOrgId.value)
    }
    catch (e) {
      console.error('Failed to revoke ticket', e)
    }
  }
}

// Consume/Deduction dialog
const isConsumeDialogOpen = ref(false)
const consumeOrgId = ref<string | null>(null)
const consumeFeature = ref<string | null>(null)
const consumeAmount = ref<number>(1)
const consumeError = ref<string | null>(null)

const openConsumeDialog = (orgId: string) => {
  consumeOrgId.value = orgId
  consumeFeature.value = null
  consumeAmount.value = 1
  consumeError.value = null
  isConsumeDialogOpen.value = true
}

const handleConsume = async () => {
  if (!consumeOrgId.value || !consumeFeature.value) {
    return
  }

  consumeError.value = null

  try {
    await $api(`/subscription/organizations/${consumeOrgId.value}/consume`, {
      method: 'POST',
      body: {
        feature: consumeFeature.value,
        amount: consumeAmount.value,
      },
    })

    await fetchOrgSubscription(consumeOrgId.value)
    isConsumeDialogOpen.value = false
  }
  catch (e: any) {
    consumeError.value = e?.data?.message || e?.message || 'Failed to deduct'
  }
}
</script>

<template>
  <VCard>
    <VCardText class="d-flex flex-wrap gap-4">
      <VTextField
        v-model="searchQuery"
        :placeholder="t('search')"
        density="compact"
        style="max-inline-size: 250px;"
      />
      <VSpacer />
    </VCardText>

    <VDataTableServer
      v-model:items-per-page="tableOptions.itemsPerPage"
      v-model:page="tableOptions.page"
      :items="items"
      :items-length="total"
      :headers="headers"
      :loading="isLoading"
      item-value="id"
    >
      <template #item.plan="{ item }">
        <span v-if="getSubscription(item.id)?.planName">
          {{ getSubscription(item.id).planName }}
        </span>
        <span
          v-else
          class="text-medium-emphasis"
        >
          {{ t(`${table}-page.no_plan`) }}
        </span>
      </template>

      <template #item.status="{ item }">
        <VChip
          v-if="getSubscription(item.id)?.status"
          :color="statusColor(getSubscription(item.id)?.status)"
          size="small"
        >
          {{ t(`${table}-page.${getSubscription(item.id)?.status}`) }}
        </VChip>
        <span
          v-else
          class="text-medium-emphasis"
        >
          -
        </span>
      </template>

      <template #item.expires_at="{ item }">
        <span v-if="getSubscription(item.id)?.expiredAt">
          {{ new Date(getSubscription(item.id).expiredAt).toLocaleDateString() }}
        </span>
        <span
          v-else
          class="text-medium-emphasis"
        >
          -
        </span>
      </template>

      <template #item.actions="{ item }">
        <VMenu>
          <template #activator="{ props: menuProps }">
            <IconBtn v-bind="menuProps">
              <VIcon icon="tabler-dots-vertical" />
            </IconBtn>
          </template>
          <VList>
            <VListItem
              v-if="!getSubscription(item.id)?.planId"
              @click="openAssignDialog(item.id)"
            >
              <VListItemTitle>{{ t(`${table}-page.assign_plan`) }}</VListItemTitle>
            </VListItem>
            <VListItem
              v-if="getSubscription(item.id)?.planId"
              @click="openAssignDialog(item.id, true)"
            >
              <VListItemTitle>{{ t(`${table}-page.switch_plan`) }}</VListItemTitle>
            </VListItem>
            <VListItem
              v-if="getSubscription(item.id)?.status === 'active'"
              @click="openCancelDialog(item.id)"
            >
              <VListItemTitle>{{ t(`${table}-page.cancel_subscription`) }}</VListItemTitle>
            </VListItem>
            <VListItem
              v-if="getSubscription(item.id)?.status === 'expired' || getSubscription(item.id)?.status === 'cancelled'"
              @click="openRenewDialog(item.id)"
            >
              <VListItemTitle>{{ t(`${table}-page.renew_subscription`) }}</VListItemTitle>
            </VListItem>
            <VListItem
              v-if="getSubscription(item.id)"
              @click="openUsageDialog(item.id)"
            >
              <VListItemTitle>{{ t(`${table}-page.view_usage`) }}</VListItemTitle>
            </VListItem>
            <VListItem @click="openTicketDialog(item.id)">
              <VListItemTitle>{{ t(`${table}-page.add_ticket`) }}</VListItemTitle>
            </VListItem>
            <VListItem @click="openConsumeDialog(item.id)">
              <VListItemTitle>{{ t(`${table}-page.deduct`) }}</VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>
      </template>

      <template #bottom>
        <VDivider />
        <div class="d-flex align-center justify-end flex-wrap gap-3 pa-5 pt-3">
          <AppSelect
            v-model="tableOptions.itemsPerPage"
            :items="[10, 25, 50, 100]"
            style="max-inline-size: 6.25rem;"
          />
          <VPagination
            v-model="tableOptions.page"
            :total-visible="5"
            :length="Math.ceil(total / tableOptions.itemsPerPage)"
          />
        </div>
      </template>
    </VDataTableServer>
  </VCard>

  <!-- Assign/Switch Plan Dialog -->
  <VDialog
    v-model="isAssignDialogOpen"
    max-width="500"
  >
    <VCard class="pa-6">
      <VCardTitle>
        {{ isSwitchMode ? t(`${table}-page.switch_plan`) : t(`${table}-page.assign_plan`) }}
      </VCardTitle>
      <VCardText>
        <VSelect
          v-model="selectedPlanId"
          :items="plans"
          item-title="name"
          item-value="id"
          :label="t(`${table}-page.current_plan`)"
          class="mb-4"
        />
        <VCheckbox
          v-if="isSwitchMode"
          v-model="switchImmediately"
          :label="t(`${table}-page.immediately`)"
        />
        <template v-if="!isSwitchMode && selectedPlanFeatures.length > 0">
          <h6 class="text-h6 mt-4 mb-2">
            {{ t(`${table}-page.initial_consumption`) }}
          </h6>
          <VTextField
            v-for="option in selectedPlanFeatures"
            :key="option.value"
            v-model.number="initialConsumption[option.value]"
            :label="option.title"
            type="number"
            :min="0"
            density="compact"
            class="mb-2"
          />
        </template>
      </VCardText>
      <VCardActions class="justify-end">
        <VBtn
          variant="tonal"
          @click="isAssignDialogOpen = false"
        >
          {{ t('cancel') }}
        </VBtn>
        <VBtn
          color="primary"
          :disabled="!selectedPlanId"
          @click="handleAssign"
        >
          {{ t('save') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Usage Dialog -->
  <VDialog
    v-model="isUsageDialogOpen"
    max-width="600"
  >
    <VCard class="pa-6">
      <VCardTitle>{{ t(`${table}-page.usage`) }}</VCardTitle>
      <VCardText>
        <div
          v-for="usage in usageData"
          :key="usage.feature"
          class="mb-4"
        >
          <div class="d-flex justify-space-between mb-1">
            <span>{{ t(`subscription.${usage.feature}`) }}</span>
            <span class="text-medium-emphasis">
              {{ t(`${table}-page.used_of`, { used: usage.used, limit: usage.limit }) }}
            </span>
          </div>
          <VProgressLinear
            :model-value="usage.limit > 0 ? (usage.used / usage.limit) * 100 : 0"
            :color="usage.limit > 0 && usage.remaining / usage.limit < 0.1 ? 'error' : 'primary'"
            rounded
            height="8"
          />
        </div>
        <p
          v-if="usageData.length === 0"
          class="text-medium-emphasis"
        >
          {{ t(`${table}-page.no_plan`) }}
        </p>

        <VDivider class="my-4" />

        <h6 class="text-h6 mb-3">
          {{ t(`${table}-page.active_tickets`) }}
        </h6>
        <VTable
          v-if="ticketsData.length > 0"
          density="compact"
        >
          <thead>
            <tr>
              <th>{{ t(`${table}-page.feature`) }}</th>
              <th>{{ t(`${table}-page.charges`) }}</th>
              <th>{{ t(`${table}-page.expires_at`) }}</th>
              <th>{{ t(`${table}-page.actions`) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="ticket in ticketsData"
              :key="ticket.id"
            >
              <td>{{ t(`subscription.${ticket.feature}`) }}</td>
              <td>{{ ticket.charges }}</td>
              <td>
                <span v-if="ticket.expiredAt">
                  {{ new Date(ticket.expiredAt).toLocaleDateString() }}
                </span>
                <span
                  v-else
                  class="text-medium-emphasis"
                >
                  {{ t(`${table}-page.no_expiry`) }}
                </span>
              </td>
              <td>
                <VBtn
                  size="small"
                  color="error"
                  variant="text"
                  @click="openRevokeTicketDialog(usageOrgId!, ticket.id)"
                >
                  {{ t(`${table}-page.revoke_ticket`) }}
                </VBtn>
              </td>
            </tr>
          </tbody>
        </VTable>
        <p
          v-else
          class="text-medium-emphasis"
        >
          -
        </p>
      </VCardText>
      <VCardActions class="justify-end">
        <VBtn @click="isUsageDialogOpen = false">
          {{ t('close') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Cancel Confirm Dialog -->
  <ConfirmDialog
    v-model:isDialogVisible="isCancelDialogOpen"
    :confirmation-question="t(`${table}-page.cancel_confirm`)"
    :confirm-title="t(`${table}-page.cancel_subscription`)"
    confirm-msg=""
    :cancel-title="t('cancel')"
    cancel-msg=""
    @confirm="handleCancel"
  />

  <!-- Renew Confirm Dialog -->
  <ConfirmDialog
    v-model:isDialogVisible="isRenewDialogOpen"
    :confirmation-question="t(`${table}-page.renew_confirm`)"
    :confirm-title="t(`${table}-page.renew_subscription`)"
    confirm-msg=""
    :cancel-title="t('cancel')"
    cancel-msg=""
    @confirm="handleRenew"
  />

  <!-- Add Ticket Dialog -->
  <VDialog
    v-model="isTicketDialogOpen"
    max-width="500"
  >
    <VCard class="pa-6">
      <VCardTitle>{{ t(`${table}-page.add_ticket`) }}</VCardTitle>
      <VCardText>
        <VSelect
          v-model="ticketFeature"
          :items="featureOptions"
          item-title="title"
          item-value="value"
          :label="t(`${table}-page.feature`)"
          class="mb-4"
        />
        <VTextField
          v-model.number="ticketCharges"
          type="number"
          :label="t(`${table}-page.charges`)"
          :min="1"
          class="mb-4"
        />
        <VTextField
          v-model="ticketExpiredAt"
          type="date"
          :label="t(`${table}-page.expired_at_optional`)"
        />
      </VCardText>
      <VCardActions class="justify-end">
        <VBtn
          variant="tonal"
          @click="isTicketDialogOpen = false"
        >
          {{ t('cancel') }}
        </VBtn>
        <VBtn
          color="primary"
          :disabled="!ticketFeature || ticketCharges < 1"
          @click="handleAddTicket"
        >
          {{ t('save') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Revoke Ticket Confirm Dialog -->
  <ConfirmDialog
    v-model:isDialogVisible="isRevokeTicketDialogOpen"
    :confirmation-question="t(`${table}-page.revoke_ticket_confirm`)"
    :confirm-title="t(`${table}-page.revoke_ticket`)"
    confirm-msg=""
    :cancel-title="t('cancel')"
    cancel-msg=""
    @confirm="handleRevokeTicket"
  />

  <!-- Consume/Deduction Dialog -->
  <VDialog
    v-model="isConsumeDialogOpen"
    max-width="500"
  >
    <VCard class="pa-6">
      <VCardTitle>{{ t(`${table}-page.deduct`) }}</VCardTitle>
      <VCardText>
        <VAlert
          v-if="consumeError"
          type="error"
          class="mb-4"
        >
          {{ consumeError }}
        </VAlert>
        <VSelect
          v-model="consumeFeature"
          :items="featureOptions"
          item-title="title"
          item-value="value"
          :label="t(`${table}-page.feature`)"
          class="mb-4"
        />
        <VTextField
          v-model.number="consumeAmount"
          type="number"
          :label="t(`${table}-page.deduct_amount`)"
          :min="1"
        />
      </VCardText>
      <VCardActions class="justify-end">
        <VBtn
          variant="tonal"
          @click="isConsumeDialogOpen = false"
        >
          {{ t('cancel') }}
        </VBtn>
        <VBtn
          color="warning"
          :disabled="!consumeFeature || consumeAmount < 1"
          @click="handleConsume"
        >
          {{ t(`${table}-page.deduct_confirm`) }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
